import { memo, useContext, useEffect, useRef, useState } from 'react';

import NormalPagination from '@lcsc-ui/components/NormalPagination';
import { LayoutContext } from '@lcsc-ui/layouts/baseLayout/provider';
import { buriedPoint } from '@lcsc-ui/utils/buriedPoint';
import { getPaginationInfo } from '@lcsc-ui/utils/common';
import lcMsgTips from '@lcsc-ui/utils/lcMsgTips';
import Utils from '@lcsc-ui/utils/Utils';
import { Popover, Tooltip } from 'antd';

import Api from '../api';
import { QbCode, SuperMemberCode, JxCode, YpCode, MoreCode, PlusCode, CouponTypeEnums } from '../enums';
import { StyledCouponBagLi, StyledCouponCardLi, StyledMoreDiv, StyledScrollUl } from '../style';

import SvgIcon from '@/components/SvgIcon';

const PageSize = 30;
const CouponsList = (props) => {
  const { resourceData } = useContext(LayoutContext);
  const { dataList, reloadCouponsStatus, couponCount, needResetPage } = props;
  const couponIdRef = useRef([]);
  const buyBtnRef = useRef(null);
  const [showMorePackage, setShowMorePackage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [ypPaginationInfo, setYpPaginationInfo] = useState(null);
  const [morePaginationInfo, setMorePaginationInfo] = useState(null);
  const [hasInitBg, setHasInitBg] = useState(true);

  useEffect(() => {
    changePagination(needResetPage ? 1 : ypPaginationInfo?.currentPage || 1, YpCode);
    changePagination(needResetPage ? 1 : morePaginationInfo?.currentPage || 1, MoreCode);
  }, [dataList, needResetPage]);

  useEffect(() => {
    setTimeout(() => {
      setHasInitBg(false);
    }, 200);
  }, []);

  const toBuy = async (item) => {
    if (loading) return false;
    setLoading(true);
    const res = await Api.buyCouponBagCheck({
      couponBagUuid: item.couponBagUuid,
    }).finally(() => {
      setLoading(false);
    });
    if (res.code === 200) {
      const url = Utils.getBtnSpmQueryUrl(
        `${resourceData?.projectConfigVO?.lcscActivityUrl}/member/couponPay.html?cbu=${item.couponBagUuid}`,
        buyBtnRef.current
      );
      window.open(url, '_blank');
    } else if (res?.needLogin === true) {
      Utils.showWindowLogin();
    } else {
      lcMsgTips.showConfirmModal({
        title: res?.msg,
      });
    }
  };

  const handleConfirmBtn = (item) => {
    const textValue =
      item.isFreeExpress && item.maxFreeExpressAmount === null
        ? '包邮'
        : item.isFreeExpress && item.maxFreeExpressAmount !== null
          ? '免邮'
          : item.couponType === CouponTypeEnums.discount
            ? item.couponDiscount / 10 + '折'
            : item.couponAmount;
    if (item.isReceive) {
      buriedPoint({
        ty: 'click',
        op: 'click_use',
        cp: 'coupon_center',
        pf: 'pc',
        data: {
          type: item.couponType,
          value: textValue,
        },
      });
      const url = Utils.getBtnSpmQueryUrl(
        item.targetUrl || resourceData?.projectConfigVO.lcscWwwUrl + (window.isMro ? '/mro' : '') + '/catalog.html',
        couponIdRef.current[item.couponId]
      );
      window.open(url, '_blank');
    } else {
      buriedPoint({
        ty: 'click',
        op: 'click_receive',
        cp: 'coupon_center',
        pf: 'pc',
        data: {
          type: item.couponType,
          value: textValue,
        },
      });
      if (loading) return false;
      setLoading(true);
      reloadCouponsStatus(item, () => {
        setLoading(false);
      });
    }
  };

  const changePagination = (page, code) => {
    const obj = dataList.find((item) => item.frontPartitionCode === code);
    if (obj) {
      const fn = {
        [YpCode]: setYpPaginationInfo,
        [MoreCode]: setMorePaginationInfo,
      }[code];
      fn(
        getPaginationInfo({
          currentUrl: '',
          currentPage: page,
          countPage: Math.ceil(obj.children.length / PageSize),
          totalCount: obj.children.length,
          pageSize: PageSize,
        })
      );
    }
  };

  const renderCouponCardTitle = ({ frontPartitionCode, frontPartitionName }) => {
    if (frontPartitionCode === SuperMemberCode || frontPartitionCode === PlusCode) {
      return (
        <>
          {frontPartitionCode === SuperMemberCode && (
            <>
              <SvgIcon
                app="index"
                iconName={'lq-super-member'}
                className="w-[60px] h-[65px] absolute left-[15px] top-0"
              />
              <SvgIcon
                app="index"
                iconName={'lq-super-member-right'}
                className="w-[298px] h-[61px] absolute left-0 top-0"
              />
              <SvgIcon
                app="index"
                iconName={'lq-super-member-right'}
                className="w-[298px] h-[61px] absolute right-0 top-0 rotate-180"
              />
            </>
          )}
          <div className={'flex justify-center items-center mb-[16px]'}>
            <SvgIcon app="index" iconName={'lq-title-stone'} className="w-[54px] h-[17px]" />
            <div className={'text-[#276FE5] text-[28px] font-semibold mx-[20px]'}>{frontPartitionName}</div>
            <SvgIcon app="index" iconName={'lq-title-stone-right'} className="w-[54px] h-[17px]" />
          </div>
        </>
      );
    }
    return (
      <div className={'flex justify-center items-center mb-[16px]'}>
        <SvgIcon app="index" iconName={'lq-title-line'} className="w-[83px] h-[10px]" />
        <div className={'text-[#444444] text-[28px] font-semibold mx-[20px]'}>{frontPartitionName}</div>
        <SvgIcon app="index" iconName={'lq-title-line'} className="w-[83px] h-[10px] rotate-180" />
      </div>
    );
  };

  const getCardColor = (value) => {
    if (value === SuperMemberCode) {
      return {
        textCls: 'text-[#4587F2]',
        btnCls: 'bg-[#4587F2] hover:bg-[#629BF7]',
        borderCls: 'border-[#4587F2]  hover:border-[#629BF7] hover:text-[#629BF7]',
        hoverBg: 'lq-hover-3',
      };
    }
    // 精选专区
    if (value === JxCode) {
      return {
        textCls: 'text-[#FF4D4F]',
        btnCls: 'bg-[#FF4D4F] hover:bg-[#FF7475]',
        borderCls: 'border-[#FF4D4F] hover:border-[#FF7475] hover:text-[#FF7475]',
        hoverBg: 'lq-hover-1',
      };
    }
    return {
      textCls: 'text-[#0093E6]',
      btnCls: 'bg-[#0093E6] hover:bg-[#45B2ED]',
      borderCls: 'border-[#0093E6] hover:border-[#45B2ED] hover:text-[#45B2ED]',
      hoverBg: 'lq-hover-2',
    };
  };

  const renderCouponCard = ({ item, index, textCls, btnCls, borderCls, hoverBg, showBtn = true }) => {
    let bgCls = '';
    const sxFlag = item.isInvalid && !item.isUse;
    if (item.isDone) {
      bgCls = 'has-lw'; // 已领完
    } else if (sxFlag) {
      bgCls = 'has-sx'; // 已失效
    } else if (item.isUse) {
      bgCls = 'has-use'; // 已使用
    }
    const primaryColorStateCls = bgCls ? 'text-[#B1B1B1]' : textCls;
    const isFree = [CouponTypeEnums.freight, CouponTypeEnums.mro_freight].includes(item.couponType);
    return (
      <StyledCouponCardLi
        key={item.couponId}
        className={`h-[100px] w-[380px] mx-[7.5px] mb-[16px] relative ${bgCls} ${!bgCls ? hoverBg : ''}`}
      >
        <div className={`${hasInitBg ? 'bg-white' : ''}  flex`}>
          <div
            className={`w-[122px] h-[100px] flex flex-col items-center ${primaryColorStateCls} text-[14px] font-semibold`}
          >
            <p className={'mt-[15px]'}>
              {isFree && item.isFreeExpress && item.maxFreeExpressAmount === null ? (
                <div className={'text-[28px] h-[42px] leading-[42px]'}>包邮</div>
              ) : (isFree && item.isFreeExpress && item.maxFreeExpressAmount) ||
                (isFree && !item.isFreeExpress && item.couponAmount) ? (
                <div className={'text-[28px]  h-[42px] leading-[42px]'}>免邮</div>
              ) : item.couponType === CouponTypeEnums.discount ? (
                <div className={'text-center flex items-end'}>
                  <div className={'text-[30px] truncate max-w-[90px] h-[42px] leading-[42px]'}>
                    {item.couponDiscount / 10}
                  </div>
                  <div className={'text-[18px] translate-y-[-6px] ml-[5px]'}>折</div>
                </div>
              ) : (
                <div className={'text-center flex items-end'}>
                  <div className={'text-[18px] translate-y-[-6px]'}>￥</div>
                  <div
                    className={'truncate max-w-[90px] text-[30px] h-[42px] leading-[42px]'}
                    title={item.couponAmount}
                  >
                    {item.couponAmount}
                  </div>
                </div>
              )}
            </p>
            <p className={'font-normal'}>
              {item.minOrderMoney > 0 ? (
                <div className={'truncate max-w-[90px]'}>
                  {item.isOrderActualMoney ? '实付满' : '满'}
                  {item.minOrderMoney}
                  {item.minOrderMoney > 99999 ? '' : '可'}用
                </div>
              ) : (
                <div className={'truncate max-w-[90px]'}>不限金额</div>
              )}
            </p>
          </div>
          <div className={'flex-1 flex text-[14px] items-center pl-[15px] pr-[16px] relative'}>
            <div className={'flex-1'}>
              <div className={'flex items-center'}>
                <div
                  className={`text-[#333333] text-[16px] mb-[4px] ${
                    !showBtn
                      ? 'max-w-[206px]'
                      : (item.couponType === CouponTypeEnums.discount && item.maxReliefAmount) ||
                          (isFree && !item.isFreeExpress && item.couponAmount) ||
                          (isFree && item.isFreeExpress && item.maxFreeExpressAmount)
                        ? 'max-w-[121px]'
                        : 'max-w-[134px]'
                  }  line-clamp-2 font-medium mr-[3px]`}
                  title={item.couponName}
                >
                  {item.useChannel === 'app' ? '移动端专享-' : ''}
                  {item.couponName}
                </div>
                {item.couponType === CouponTypeEnums.discount && item.maxReliefAmount && (
                  <Tooltip
                    title={'最高可抵扣' + item.maxReliefAmount + '元'}
                    placement="bottom"
                    overlayStyle={{ paddingTop: 8 }}
                  >
                    <img src="//static.szlcsc.com/ecp/assets/web/page/www/get-coupons/images/help.svg" alt="tip" />
                  </Tooltip>
                )}
                {isFree && !item.isFreeExpress && item.couponAmount && (
                  <Tooltip
                    title={'最高可抵扣' + item.couponAmount + '元运费'}
                    placement="bottom"
                    overlayStyle={{ paddingTop: 8 }}
                  >
                    <img src="//static.szlcsc.com/ecp/assets/web/page/www/get-coupons/images/help.svg" alt="tip" />
                  </Tooltip>
                )}
                {isFree && item.isFreeExpress && item.maxFreeExpressAmount && (
                  <Tooltip
                    title={'最高可抵扣' + item.maxFreeExpressAmount + '元运费'}
                    placement="bottom"
                    overlayStyle={{ paddingTop: 8 }}
                  >
                    <img src="//static.szlcsc.com/ecp/assets/web/page/www/get-coupons/images/help.svg" alt="tip" />
                  </Tooltip>
                )}
              </div>
              <div className={'flex'}>
                <div className={'text-[#999999] text-[12px] mr-[3px]'}>已领 {item.receiveCustomerNum}</div>
                {item.customerMaxNum && item.customerMaxNum !== 1 && (
                  <Tooltip
                    title={
                      item.isDayLimit
                        ? '单客编限领取：' + `${item.customerMaxNum}张/天`
                        : '单客编限领取：' + `${item.customerMaxNum}/张`
                    }
                    placement="bottom"
                    overlayStyle={{ paddingTop: 8 }}
                  >
                    <img src="//static.szlcsc.com/ecp/assets/web/page/www/get-coupons/images/tip.svg" alt="tip" />
                  </Tooltip>
                )}
              </div>
            </div>
            {!bgCls && showBtn && (
              <button
                data-spm={index + 1}
                onClick={() => handleConfirmBtn(item)}
                ref={(el) => (couponIdRef.current[item.couponId] = el)}
                className={`${
                  item.isReceive ? textCls + ' border border-solid ' + borderCls : btnCls + ' text-[#ffffff]'
                } cursor-pointer w-[72px] h-[30px] leading-[30px] rounded-[4px]`}
              >
                {item.isReceive ? '去使用' : '立即抢券'}
              </button>
            )}
            {item.isShowCouponLabel && !bgCls && (
              <div className={'absolute z-[2] text-center w-[64px] h-[27px] text-[12px]  top-[10px] right-[8px]'}>
                <div className={'relative'}>
                  <img src={'//static.szlcsc.com/ecp/assets/newWeb/www/lingquan/lq-title-four-bg.svg'} alt="tip" />
                  <div
                    className={
                      'absolute left-[0px] top-[0px] px-[8px] w-[64px] text-[#FF4D4F] h-[22px] leading-[22px] truncate text-center'
                    }
                  >
                    {item.couponLabelName}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        {item.isReceive && !bgCls && (
          <div
            className={
              'absolute  bg-[url(//static.szlcsc.com/ecp/assets/newWeb/www/lingquan/lq-has-receive.svg)] bg-[length:100%_100%] bg-no-repeat w-[45px] h-[50px] left-0 top-[-2px] text-[12px] text-white'
            }
          >
            <span className={'rotate-[-45deg] inline-block translate-y-[7px] translate-x-[5px]'}>已领</span>
          </div>
        )}
      </StyledCouponCardLi>
    );
  };

  const getPopoverContent = (item, clsObj) => {
    return (
      <>
        <div className={'font-bold text-[24px] p-[15px]'}>{item.couponBagName}</div>
        <StyledScrollUl className={'max-h-[450px] overflow-y-auto scrollbar-custom px-[7.5px]'}>
          {item?.couponList.slice(2).map((v, i) => {
            return renderCouponCard({
              item: v,
              index: i,
              showBtn: false,
              ...clsObj,
            });
          })}
        </StyledScrollUl>
      </>
    );
  };
  const renderCouponsPackage = (item, clsObj) => {
    if (!item?.children || item?.children.length === 0) return <></>;
    const len = item.children.length;
    const children = showMorePackage ? item?.children : item?.children.slice(0, 1);
    return (
      <ul data-spm="gq">
        {children.map((v, i) => {
          let bgCls = '';
          if (v.status === 'already_buy') {
            bgCls = 'has-buy';
          }
          if (v.status !== 'buy_now' && v.status !== 'already_buy') {
            bgCls = 'has-sale';
          }
          return (
            <StyledCouponBagLi key={i} className={`mb-[14px] relative w-[1160px] h-[206px] ${bgCls}`}>
              <div className={'flex h-[206px]'}>
                <div className={'flex-1 pl-[24px] pt-[24px] flex flex-col'}>
                  <div className={'flex text-[#333333] items-center mb-[23px]'}>
                    <div className={'font-bold text-[24px]'} title={v.couponBagName}>
                      {v.couponBagName}
                    </div>
                    <div className={'text-[16px]'}>（{v.intro}）</div>
                  </div>
                  <ul className={'flex'}>
                    {v?.couponList.slice(0, 2).map((vv, ii) => {
                      return renderCouponCard({
                        item: vv,
                        index: ii,
                        showBtn: false,
                        ...clsObj,
                      });
                    })}
                    {v?.couponList.length > 2 && (
                      <Popover
                        content={() => getPopoverContent(v, clsObj)}
                        title=""
                        trigger="hover"
                        placement="bottom"
                        overlayClassName="lcsc-popover-wrapper"
                        zIndex={999}
                      >
                        <div className={'relative w-[100px] h-[85px]'}>
                          <div
                            className={
                              'w-[100px] h-[85px] rounded-[8px] bg-white border-[#E66C58] border-solid border absolute left-0 top-0 z-[8]'
                            }
                          ></div>
                          <div
                            className={
                              'w-[100px] h-[85px] rounded-[8px] bg-white border-[#E66C58] border-solid border absolute left-[6px] top-[6px] z-[7]'
                            }
                          ></div>
                          <div
                            className={
                              'w-[100px] h-[85px] rounded-[8px] bg-white border-[#E66C58] border-solid border  absolute left-[12px] top-[12px] z-[6]'
                            }
                          ></div>
                          <div
                            className={
                              'absolute left-[50%] top-[50%] w-[px] cursor-pointer truncate translate-x-[-50%] translate-y-[-50%] text-[#FC503C] text-[14px]  z-[9]'
                            }
                          >
                            剩余{v.couponList.length - 2}张券 {'>'}
                          </div>
                        </div>
                      </Popover>
                    )}
                  </ul>
                </div>
                <div className={'w-[236px] flex items-center justify-center flex-col'}>
                  <div className={'mb-[10px] cursor-default'}>
                    <span className={'text-[#F44242] text-[32px] font-bold'}>{v.sellPrice}</span>
                    <span className={'text-[#F44242] text-[26px] font-bold'}>元</span>
                    <span className={'text-[#999999] text-[16px] font-medium pl-[4px] line-through'}>
                      原价{v.originalPrice}元
                    </span>
                  </div>
                  {v.status === 'buy_now' ? (
                    <button
                      data-spm={i + 1}
                      className={
                        'w-[120px] hover:bg-[#F77A7A] h-[36px] cursor-pointer leading-[36px] rounded-[100px] bg-[#F44242] text-[#FFFFFF] text-center font-normal text-[16px]'
                      }
                      ref={buyBtnRef}
                      onClick={() => toBuy(v)}
                    >
                      立即购买
                    </button>
                  ) : v.status === 'already_buy' ? (
                    <div
                      className={
                        'w-[120px] h-[36px]  leading-[36px] rounded-[100px] bg-[#FFE0E0] text-[#F44242] text-center font-normal text-[16px]'
                      }
                    >
                      您已购买
                    </div>
                  ) : (
                    <div
                      className={
                        'w-[120px] h-[36px]  leading-[36px] rounded-[100px] bg-[#F44242] text-[#FFFFFF] text-center font-normal text-[16px] opacity-40'
                      }
                    >
                      已售罄
                    </div>
                  )}
                  {v.ruleDescription && (
                    <Popover
                      content={
                        <div
                          className={'w-[400px] max-h-[450px] overflow-y-auto'}
                          dangerouslySetInnerHTML={{
                            __html: v.ruleDescription,
                          }}
                        ></div>
                      }
                      title=""
                      trigger="hover"
                      placement="bottom"
                      align={{
                        offset: [0, -4],
                      }}
                      autoAdjustOverflow={false}
                      getPopupContainer={(triggerNode) => triggerNode.parentNode}
                    >
                      <div className={'mt-[10px] text-[#999999] text-[12px] cursor-pointer flex items-center'}>
                        <span className={'pr-[4px]'}>了解权益规则</span>
                        <img
                          src="//static.szlcsc.com/ecp/assets/web/page/www/get-coupons/images/arrow.svg"
                          alt="更多"
                          className={'size-[10px]'}
                        />
                      </div>
                    </Popover>
                  )}
                </div>
              </div>
              <div
                className={
                  'absolute right-[-10px] top-[15px] text-[20px] text-[#FFFFFF] w-[80px] text-center rotate-[50deg]'
                }
              >
                {v?.couponList.length}张
              </div>
            </StyledCouponBagLi>
          );
        })}
        {len > 1 && !showMorePackage && (
          <StyledMoreDiv
            className={
              'text-[#666666] more hover:text-[#0093E6] text-[12px] mt-[24px] mb-[20px] text-center m-auto w-full cursor-pointer'
            }
            onClick={() => setShowMorePackage(true)}
          >
            <span className={'pr-[4px]'}>查看更多券包</span>
            <span className={`inline-block up-arrow-icon size-[10px]`}></span>
          </StyledMoreDiv>
        )}
      </ul>
    );
  };

  const renderPagination = (item) => {
    if (item.frontPartitionCode === YpCode && ypPaginationInfo?.countPage > 1) {
      return (
        <div className={'mt-[5px] mb-[32px]'}>
          <NormalPagination
            paginationInfo={ypPaginationInfo}
            changePagination={(page) => {
              changePagination(page, YpCode);
            }}
            showJumpBtn
          />
        </div>
      );
    }
    if (item.frontPartitionCode === MoreCode && morePaginationInfo?.countPage > 1) {
      return (
        <div className={'mt-[5px] mb-[32px]'}>
          <NormalPagination
            paginationInfo={morePaginationInfo}
            changePagination={(page) => {
              changePagination(page, MoreCode);
            }}
            showJumpBtn
          />
        </div>
      );
    }
    return <></>;
  };

  return (
    <>
      <section
        className={
          'flex justify-end my-[16px] px-[20px] bg-white h-[40px] items-center rounded-[8px] text-[14px] text-[#000000]'
        }
        id={'我的优惠券-content'}
      >
        <div className={'flex-1 flex items-center'}>
          <SvgIcon app="index" iconName={'lq-my-yhq'} className="w-[16px] h-[14px] mr-[8px]" />
          <a
            className={'cursor-pointer'}
            href={`${resourceData?.projectConfigVO?.lcscMemberUrl}/member/couponList.html`}
          >
            <span>我的优惠券</span>（<span className={'text-[#0093E6]'}>{couponCount}张</span>）
          </a>
        </div>
        <a
          className={'cursor-pointer  text-[#0093E6]'}
          href={`${resourceData?.projectConfigVO?.lcscMemberUrl}/member/couponList.html`}
        >
          <div className={'flex  items-center'}>
            <div>查看详情</div>
            <SvgIcon
              app="index"
              iconName={'lq-arrow-active'}
              className="w-[10px] h-[8px] ml-[4px] rotate-[-90deg] translate-y-[0px]"
            />
          </div>
        </a>
      </section>
      {dataList.map((item, index) => {
        let list = item.children;
        if (item.frontPartitionCode === YpCode && ypPaginationInfo) {
          const startIndex = (ypPaginationInfo?.currentPage - 1) * PageSize;
          const endIndex = ypPaginationInfo?.currentPage * PageSize;
          list = list.slice(startIndex, endIndex);
        }
        if (item.frontPartitionCode === MoreCode && morePaginationInfo) {
          const startIndex = (morePaginationInfo?.currentPage - 1) * PageSize;
          const endIndex = morePaginationInfo?.currentPage * PageSize;
          list = list.slice(startIndex, endIndex);
        }
        return (
          <section
            key={index}
            id={`${item.frontPartitionName}-content`}
            data-spm={item.frontPartitionCode}
            className={`${item.frontPartitionCode === QbCode ? 'px-[20px]' : 'pl-[7.5px]'}  ${item.frontPartitionCode === SuperMemberCode ? 'relative' : ''} pb-[4px]  pt-[15px] rounded-[10px] mb-[30px]`}
            style={{
              background:
                item.frontPartitionCode === SuperMemberCode
                  ? 'linear-gradient(180deg, #DBECFF 0%, #FFFFFF 100%)'
                  : '#FFFFFF',
            }}
          >
            {renderCouponCardTitle(item)}
            {item.frontPartitionCode === QbCode ? (
              renderCouponsPackage(item, getCardColor(item.frontPartitionCode))
            ) : (
              <>
                <ul className={'flex flex-wrap'}>
                  {list.map((v, i) => {
                    return renderCouponCard({
                      item: v,
                      index: i,
                      ...getCardColor(item.frontPartitionCode),
                    });
                  })}
                </ul>
                {renderPagination(item)}
              </>
            )}
          </section>
        );
      })}
    </>
  );
};

export default memo(CouponsList);
